import { API_CONFIG } from '@/lib/api/config';
import { ApiError } from '@/lib/api/client';
import { cachedConnectionCheck, clearConnectionCheckCache } from '@/lib/api/connectionCheckCache';
import { scopedFetch } from '@/lib/api/scopedFetch';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
});

/** Mirrors RazorpayPaymentBackfillService.describeStatus. */
export type RazorpayBackfillStatus = {
  /** Raw job status. 'none' when the workspace has never run an import. */
  status: 'none' | 'pending' | 'running' | 'completed' | 'failed' | 'paused';
  /** Coarse bucket for rendering. 'failed' with retries left is in_progress. */
  state: 'never_started' | 'in_progress' | 'complete' | 'needs_attention';
  /** The one answer the Sync control reads. Never re-derive it from `status`. */
  canSync: boolean;
  syncBlockedReason: 'backfill_not_started' | 'backfill_in_progress' | 'backfill_needs_attention' | null;
  progress: {
    paymentsImported: number;
    pagesProcessed: number;
    /** 0-99 while walking; 100 only once the job itself says complete. */
    percent: number;
    /** How far back the walk has reached, ISO. */
    oldestImportedAt: string | null;
    /** Minutes since the last real progress (or since the job was created). */
    quietForMinutes: number | null;
    /**
     * In progress, but nothing has moved for a while. Recovery is automatic;
     * this exists so the UI stops claiming an import is running when it is not.
     */
    stalled: boolean;
  };
  coverage: {
    scope: 'full' | 'boundary' | null;
    oldestCoveredAt: string | null;
    /** true = older payments exist at Razorpay and were NOT imported. */
    limitedByCutoff: boolean | null;
    cutoffDays: number;
  };
  /**
   * The forward sweep that keeps the table current once history is in.
   * Surfaced because a merchant with no webhook secret has no other signal
   * that payments are still arriving.
   */
  liveSync: {
    lastCheckedAt: string | null;
    paymentsSynced: number;
    errorCode: string | null;
    trailMinutes: number;
  };
  startedAt: string | null;
  lastProgressAt: string | null;
  completedAt: string | null;
  /** Code only — the raw provider message is deliberately not sent to the browser. */
  error: { code: string; willRetryItself: boolean; attempts: number; attemptsExhausted: boolean } | null;
};

export const razorpayApi = {
  async connect(payload: {
    businessId: string;
    keyId: string;
    keySecret: string;
    accountName?: string;
    webhookSecret?: string;
  }) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/connect`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to connect Razorpay');
    }
    clearConnectionCheckCache(`razorpay:${payload.businessId}`);
    return json;
  },

  async disconnect(businessId: string, connectionType?: 'manual' | 'oauth') {
    const url = `${API_CONFIG.BASE_URL}/razorpay/${businessId}/disconnect${connectionType ? `?connectionType=${connectionType}` : ''}`;
    const res = await scopedFetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to disconnect Razorpay');
    }
    clearConnectionCheckCache(`razorpay:${businessId}`);
    return json;
  },

  async checkConnection(businessId: string) {
    return cachedConnectionCheck(`razorpay:${businessId}`, async () => {
      const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/connection`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to check Razorpay connection');
      }
      return json;
    });
  },

  /*
   * getOAuthUrl / completeOAuthCallback were removed: no caller, and the
   * endpoints behind them are gone too. Razorpay is connected by pasting a
   * key id and secret — see connect() above.
   */

  async refreshToken(businessId: string) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/refresh-token`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to refresh token');
    }
    return json;
  },

  async testConnection(businessId: string) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/test`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to test connection');
    }
    return json;
  },

  async createOrder(businessId: string, payload: {
    amount: number;
    currency?: string;
    receipt?: string;
    notes?: Record<string, any>;
    customerId?: string;
  }) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/orders`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to create order');
    }
    return json;
  },

  async getOrder(businessId: string, orderId: string) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/orders/${orderId}`, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to get order');
    }
    return json;
  },

  async listOrders(businessId: string, params?: {
    count?: number;
    skip?: number;
    from?: number;
    to?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.count) query.append('count', params.count.toString());
    if (params?.skip) query.append('skip', params.skip.toString());
    if (params?.from) query.append('from', params.from.toString());
    if (params?.to) query.append('to', params.to.toString());
    const url = `${API_CONFIG.BASE_URL}/razorpay/${businessId}/orders${query.toString() ? `?${query.toString()}` : ''}`;
    const res = await scopedFetch(url, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to list orders');
    }
    return json;
  },

  async getPayment(businessId: string, paymentId: string) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/payments/${paymentId}`, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to get payment');
    }
    return json;
  },

  async listPayments(businessId: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }) {
    const query = new URLSearchParams();
    query.append('page', (params?.page || 1).toString());
    query.append('limit', (params?.limit || 20).toString());
    if (params?.status) query.append('status', params.status);
    if (params?.search) query.append('search', params.search);
    const url = `${API_CONFIG.BASE_URL}/razorpay/${businessId}/payments${query.toString() ? `?${query.toString()}` : ''}`;
    const res = await scopedFetch(url, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to list payments');
    }
    return json;
  },

  /**
   * State of the historical payment import.
   *
   * One source for two consumers: the progress/coverage banner and the Sync
   * control's enablement. Reading `canSync` rather than re-deriving it from
   * `status` is what stops the banner and the button disagreeing.
   */
  async getPaymentBackfillStatus(businessId: string): Promise<RazorpayBackfillStatus | null> {
    const url = `${API_CONFIG.BASE_URL}/razorpay/${businessId}/payments/backfill-status`;
    const res = await scopedFetch(url, { headers: getAuthHeaders() });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to read import status');
    }
    return json?.backfill ?? null;
  },

  /**
   * Ask the forward sweep to run now instead of at its next schedule.
   *
   * Returns as soon as the run is queued, with the counters to compare
   * against once it lands — the caller watches getPaymentBackfillStatus.
   * `count`/`skip` are gone: the sweep is cursor-driven and paginates itself,
   * and the old parameters only ever described the single newest page.
   */
  async syncPayments(businessId: string): Promise<{
    success: boolean;
    started?: boolean;
    paymentsSyncedBefore?: number;
    lastCheckedAtBefore?: string | null;
  }> {
    const url = `${API_CONFIG.BASE_URL}/razorpay/${businessId}/payments/sync`;
    const res = await scopedFetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to sync payments');
    }
    return json;
  },

  async capturePayment(businessId: string, paymentId: string, amount: number) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/payments/${paymentId}/capture`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ amount }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to capture payment');
    }
    return json;
  },

  async verifyPayment(businessId: string, payload: {
    razorpayOrderId: string;
    razorpayPaymentId: string;
    razorpaySignature: string;
  }) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/payments/verify`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to verify payment');
    }
    return json;
  },

  async createCustomer(businessId: string, payload: {
    name: string;
    email?: string;
    contact?: string;
    notes?: Record<string, any>;
  }) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/customers`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to create customer');
    }
    return json;
  },

  async getCustomer(businessId: string, customerId: string) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/customers/${customerId}`, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to get customer');
    }
    return json;
  },

  async updateCustomer(businessId: string, customerId: string, payload: {
    name?: string;
    email?: string;
    contact?: string;
    notes?: Record<string, any>;
  }) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/customers/${customerId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to update customer');
    }
    return json;
  },

  async listCustomers(businessId: string, params?: {
    count?: number;
    skip?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.count) query.append('count', params.count.toString());
    if (params?.skip) query.append('skip', params.skip.toString());
    const url = `${API_CONFIG.BASE_URL}/razorpay/${businessId}/customers${query.toString() ? `?${query.toString()}` : ''}`;
    const res = await scopedFetch(url, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to list customers');
    }
    return json;
  },


  async createPaymentLink(businessId: string, payload: {
    amount: number;
    currency?: string;
    description?: string;
    customer?: {
      name?: string;
      email?: string;
      contact?: string;
    };
    notify?: {
      sms?: boolean;
      email?: boolean;
    };
    reminder_enable?: boolean;
    callback_url?: string;
    callback_method?: 'get' | 'post';
    notes?: Record<string, any>;
    expire_by?: number;
  }) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/payment-links`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to create payment link');
    }
    return json;
  },

  async getPaymentLink(businessId: string, paymentLinkId: string) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/payment-links/${paymentLinkId}`, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to get payment link');
    }
    return json;
  },

  // Razorpay list APIs page with count/skip, not page/limit — same contract as
  // listPlans / listSubscriptions above. The previous page/limit signature could
  // not reach this endpoint: those names are forwarded verbatim to Razorpay,
  // which rejects unknown params ("page, limit is/are not required"). Only
  // `{}` ever worked, which silently capped the tab at Razorpay's default of
  // 10 links.
  async listPaymentLinks(businessId: string, params?: {
    count?: number;
    skip?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.count) query.append('count', params.count.toString());
    if (params?.skip) query.append('skip', params.skip.toString());
    const url = `${API_CONFIG.BASE_URL}/razorpay/${businessId}/payment-links${query.toString() ? `?${query.toString()}` : ''}`;
    const res = await scopedFetch(url, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to list payment links');
    }
    return json;
  },

  async syncPaymentLinks(businessId: string, params?: {
    count?: number;
    skip?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.count) query.append('count', params.count.toString());
    if (params?.skip) query.append('skip', params.skip.toString());
    const url = `${API_CONFIG.BASE_URL}/razorpay/${businessId}/payment-links/sync${query.toString() ? `?${query.toString()}` : ''}`;
    const res = await scopedFetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to sync payment links');
    }
    return json;
  },

  async createPlan(businessId: string, payload: {
    period: 'daily' | 'weekly' | 'monthly' | 'yearly';
    interval: number;
    item: {
      name: string;
      amount: number;
      currency?: string;
      description?: string;
    };
    notes?: Record<string, any>;
  }) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/plans`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to create plan');
    }
    return json;
  },

  async getPlan(businessId: string, planId: string) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/plans/${planId}`, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to get plan');
    }
    return json;
  },

  async listPlans(businessId: string, params?: {
    count?: number;
    skip?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.count) query.append('count', params.count.toString());
    if (params?.skip) query.append('skip', params.skip.toString());
    const url = `${API_CONFIG.BASE_URL}/razorpay/${businessId}/plans${query.toString() ? `?${query.toString()}` : ''}`;
    const res = await scopedFetch(url, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to list plans');
    }
    return json;
  },

  async listOffers(businessId: string) {
    const url = `${API_CONFIG.BASE_URL}/razorpay/${businessId}/offers`;
    const res = await scopedFetch(url, { headers: getAuthHeaders() });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to list offers');
    }
    return json;
  },

  async createSubscription(businessId: string, payload: {
    planId: string;
    totalCount: number;
    quantity?: number;
    customerNotify?: number;
    notes?: Record<string, any>;
    customerId?: string;
    expireBy?: number;
    offerId?: string;
  }) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/subscriptions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to create subscription');
    }
    return json;
  },

  async getSubscription(businessId: string, subscriptionId: string) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/subscriptions/${subscriptionId}`, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to get subscription');
    }
    return json;
  },

  async listSubscriptions(businessId: string, params?: {
    count?: number;
    skip?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.count) query.append('count', params.count.toString());
    if (params?.skip) query.append('skip', params.skip.toString());
    const url = `${API_CONFIG.BASE_URL}/razorpay/${businessId}/subscriptions${query.toString() ? `?${query.toString()}` : ''}`;
    const res = await scopedFetch(url, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to list subscriptions');
    }
    return json;
  },

  async cancelSubscription(businessId: string, subscriptionId: string, cancelAtCycleEnd?: boolean) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/subscriptions/${subscriptionId}/cancel`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ cancelAtCycleEnd }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to cancel subscription');
    }
    return json;
  },

  async pauseSubscription(businessId: string, subscriptionId: string) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/subscriptions/${subscriptionId}/pause`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to pause subscription');
    }
    return json;
  },

  async resumeSubscription(businessId: string, subscriptionId: string) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/subscriptions/${subscriptionId}/resume`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to resume subscription');
    }
    return json;
  },

  async cancelPaymentLink(businessId: string, paymentLinkId: string) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/razorpay/${businessId}/payment-links/${paymentLinkId}/cancel`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to cancel payment link');
    }
    return json;
  },
};

